import React, { useCallback, useEffect, useState } from "react";
import useCompanyDateFormat from "../../hooks/useCompanyDateFormat";
import { Table, Button, Space, Tag, Modal, Input, message, Empty, Typography } from "antd";
import { CheckOutlined, CloseOutlined, FilePdfOutlined } from "@ant-design/icons";
import moment from "moment";
import Service from "../../service";
import "./ReviewForms.css";
import { TABLE_PAGINATION } from "../../constants/tablePagination";

const { TextArea } = Input;
const { Text } = Typography;

/**
 * Review forms awaiting this viewer's decision.
 *
 * One approval closes the form: either the employee's Reporting Manager or any
 * Admin, whoever acts first. There is no chain and no second level - see
 * docs/REVIEW_FORMS_MODULE.md §7. The server decides the scope (an Admin sees
 * the whole company, a manager sees their own reports), so this screen is the
 * same for both.
 */
const ReviewFormApprovals = () => {
  const dateFormat = useCompanyDateFormat();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null); // { row, action }
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.reviewFormPendingApprovals,
      });
      setRows(response?.data?.status === 200 ? response.data.data || [] : []);
    } catch (error) {
      message.error("Could not load pending review forms");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Open the submitted PDF so it can be read before deciding.
   *
   * Fetched rather than linked: the stream endpoint sits behind the same Bearer
   * token as everything else, and a bare `window.open` sends no Authorization
   * header - the tab would just show a 401. The response is turned into a blob
   * URL, which the browser renders in its own viewer.
   */
  const viewPdf = async (row) => {
    if (!row.currentSubmission) return;
    try {
      const response = await fetch(
        `${Service.API_URL}${Service.reviewFormSubmission}/${row._id}/${row.currentSubmission._id}`,
        { headers: { authorization: `Bearer ${localStorage.getItem("accessToken")}` } }
      );
      if (!response.ok) {
        message.error("Could not open the submitted form");
        return;
      }
      const url = URL.createObjectURL(await response.blob());
      window.open(url, "_blank", "noopener");
      // Revoked on a delay: revoking immediately can race the new tab's own
      // load and leave the viewer blank.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      message.error("Could not open the submitted form");
    }
  };

  const submitDecision = async () => {
    if (!decision) return;
    if (decision.action === "reject" && !comment.trim()) {
      message.error("Please say why the form is being rejected");
      return;
    }
    try {
      setSubmitting(true);
      const api =
        decision.action === "approve" ? Service.reviewFormApprove : Service.reviewFormReject;
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: `${api}/${decision.row._id}`,
        body: { comment: comment.trim() },
      });
      if (response?.data?.status === 200) {
        message.success(response.data.message);
        setDecision(null);
        setComment("");
        load();
      } else {
        message.error(response?.data?.message || "Could not record the decision");
      }
    } catch (error) {
      message.error("Could not record the decision");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: "Employee",
      key: "employee",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.employee?.full_name || "-"}</Text>
          {row.employee?.emp_code && <Text type="secondary">{row.employee.emp_code}</Text>}
        </Space>
      ),
    },
    { title: "Form", dataIndex: "milestoneLabel", key: "milestone" },
    {
      title: "Due",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (value, row) => (
        <Space size={4}>
          {moment(value).format(dateFormat)}
          {row.isOverdue && <Tag color="red">Overdue</Tag>}
        </Space>
      ),
    },
    {
      title: "Submitted",
      dataIndex: "firstSubmittedAt",
      key: "submitted",
      render: (value) => (value ? moment(value).format("DD MMM, h:mm A") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Space wrap>
          <Button
            size="small"
            icon={<FilePdfOutlined />}
            onClick={() => viewPdf(row)}
            disabled={!row.currentSubmission}
          >
            View PDF
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<CheckOutlined />}
            // canAct is set for Sub-Admin only: false outside its own team.
            disabled={row.canAct === false}
            onClick={() => {
              setDecision({ row, action: "approve" });
              setComment("");
            }}
          >
            Approve
          </Button>
          <Button
            size="small"
            danger
            icon={<CloseOutlined />}
            // canAct is set for Sub-Admin only: false outside its own team.
            disabled={row.canAct === false}
            onClick={() => {
              setDecision({ row, action: "reject" });
              setComment("");
            }}
          >
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="review-forms-page">
      <Table
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        locale={{ emptyText: <Empty description="No review forms are waiting for you" /> }}
        pagination={{ ...TABLE_PAGINATION }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title={
          decision?.action === "approve"
            ? `Approve ${decision?.row?.milestoneLabel}`
            : `Reject ${decision?.row?.milestoneLabel}`
        }
        open={!!decision}
        onOk={submitDecision}
        onCancel={() => setDecision(null)}
        okText={decision?.action === "approve" ? "Approve" : "Reject"}
        okButtonProps={{ danger: decision?.action === "reject", loading: submitting }}
      >
        <p>
          {decision?.row?.employee?.full_name} - {decision?.row?.milestoneLabel}
        </p>
        <TextArea
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={
            decision?.action === "reject"
              ? "Why is this being rejected? (required)"
              : "Any comments (optional)"
          }
        />
        {decision?.action === "reject" && (
          <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
            The employee will see this comment.
          </Text>
        )}
      </Modal>
    </div>
  );
};

export default ReviewFormApprovals;
